import { api } from './api';

export interface LookAtThatExploreItem {
  id: string;
  prompt: string;
  explanation: string;
  file: string;
  ts: string;
  sessionId?: string;
}

export interface LookAtThatCalibration {
  capturedAt: string;
  screens: { id: string; file: string; label?: string }[];
  controls: string[];
}

export interface LookAtThatMemoryNote {
  id: string;
  ts: string;
  source: string;
  testKey?: string;
  text: string;
}

export interface LookAtThatQueue {
  current: string | null;
  pending: string[];
}

export interface LookAtThatChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  ts: string;
}

export interface LookAtThatAdapter {
  id: string;
  label: string;
  model: string;
  configured: boolean;
}

export interface LookAtThatStatus {
  engine: { ok: boolean; error?: string };
  adapters?: LookAtThatAdapter[];
  sessionId: string | null;
  defaultGameUrl: string | null;
  recipeUser: string | null;
  hasRecipePassword: boolean;
  explore: LookAtThatExploreItem[];
  calibration: LookAtThatCalibration | null;
  memory: LookAtThatMemoryNote[];
  playQueue: LookAtThatQueue;
  chat: LookAtThatChatMessage[];
}

export const lookAtThatApi = {
  status: (runId: string) => api.get<LookAtThatStatus>(`/api/runs/${runId}/look-at-that`),
  start: (runId: string, body: { gameUrl: string; task?: string; adapter?: string }) =>
    api.post<{ sessionId: string; status: string; adapter?: string }>(`/api/runs/${runId}/look-at-that/session`, body),
  execute: (runId: string, sessionId: string, task: string) =>
    api.post(`/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/execute`, { task }),
  explore: (runId: string, sessionId: string, prompt: string) =>
    api.post<{ accepted?: boolean }>(
      `/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/explore`,
      { prompt },
    ),
  credentials: (runId: string, sessionId: string, body: { user: string; password: string; extra?: string }) =>
    api.post(`/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/credentials`, body),
  stop: (runId: string, sessionId: string) =>
    api.post(`/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/stop`),
  refresh: (runId: string, sessionId: string) =>
    api.post(`/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/refresh`),
  chat: (runId: string, sessionId: string, message: string) =>
    api.post<{ accepted?: boolean; message?: LookAtThatChatMessage }>(
      `/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/chat`,
      { message },
    ),
  calibrate: (runId: string, sessionId: string) =>
    api.post<{ accepted?: boolean }>(
      `/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/calibrate`,
      {},
    ),
  play: (runId: string, sessionId: string, testKeys: string[]) =>
    api.post<{ accepted?: boolean; queue?: LookAtThatQueue }>(
      `/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/play`,
      { testKeys },
    ),
  playStop: (runId: string, sessionId: string) =>
    api.post(`/api/runs/${runId}/look-at-that/session/${encodeURIComponent(sessionId)}/play/stop`, {}),
  memory: (runId: string) =>
    api.get<LookAtThatMemoryNote[]>(`/api/runs/${runId}/look-at-that/memory`),
  deleteMemoryNote: (runId: string, noteId: string) =>
    api.delete<LookAtThatMemoryNote[]>(
      `/api/runs/${runId}/look-at-that/memory/${encodeURIComponent(noteId)}`,
    ),
  exploreImageUrl: (runId: string, file: string) =>
    `/api/runs/${runId}/look-at-that/explore/${encodeURIComponent(file)}`,
  calibrationImageUrl: (runId: string, file: string) =>
    `/api/runs/${runId}/look-at-that/calibration/${encodeURIComponent(file)}`,
};
